/**
 * KeyboardShortcutsHelp · Drawer (not modal) listing all keyboard shortcuts.
 *
 * Opens when "?" is pressed via useKeyboardShortcuts.
 * Implemented as a slide-in drawer from the right — no modal backdrop lock,
 * no focus trap (can still type while it's open).
 *
 * DESIGN.md: Drawers use translateX + 280ms cubic-bezier(0.32, 0.72, 0, 1).
 */

import { useEffect, useRef } from 'react';
import { cn } from '@/lib/cn';
import { useT } from '@/hooks/useT';

// ─── Inline SVG icons ─────────────────────────────────────────────────────────
function XIcon({ size = 14 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden>
      <path d="M2 2l10 10M12 2l-10 10" strokeLinecap="round" />
    </svg>
  );
}
function CommandIcon({ size = 15, className }: { size?: number; className?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 15 15" fill="none" stroke="currentColor" strokeWidth="1.4" aria-hidden className={className}>
      <path d="M3.5 9.5a2 2 0 1 0 0-4 2 2 0 0 0 0 4zm8 0a2 2 0 1 0 0-4 2 2 0 0 0 0 4z" />
      <path d="M5.5 7.5h4m-6-4v3h3v3m6-6v3h-3v3" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// ─── Shortcut data ─────────────────────────────────────────────────────────────

interface ShortcutEntry {
  label: string;
  keys: string[];
}

interface ShortcutGroup {
  title: string;
  entries: ShortcutEntry[];
}

const isMac =
  typeof navigator !== 'undefined' &&
  /Mac|iPhone|iPad/.test(navigator.platform ?? navigator.userAgent);

const MOD = isMac ? '⌘' : 'Ctrl';

function getShortcutGroups(isEn: boolean): ShortcutGroup[] {
  return [
    {
      title: isEn ? 'Search' : 'Pesquisa',
      entries: [
        { label: isEn ? 'Global search' : 'Busca global', keys: [`${MOD}`, 'K'] },
        { label: isEn ? 'Search in list' : 'Busca na lista', keys: ['/'] },
      ],
    },
    {
      title: isEn ? 'Navigation' : 'Navegação',
      entries: [
        { label: isEn ? 'Go to Today' : 'Ir para Hoje', keys: ['G', 'H'] },
        { label: isEn ? 'Go to Sites' : 'Ir para Obras', keys: ['G', 'P'] },
        { label: isEn ? 'Go to Inbox' : 'Ir para Inbox', keys: ['G', 'I'] },
        { label: isEn ? 'Go to Notifications' : 'Ir para Notificações', keys: ['G', 'N'] },
      ],
    },
    {
      title: isEn ? 'General' : 'Geral',
      entries: [
        { label: isEn ? 'View shortcuts' : 'Ver atalhos', keys: ['?'] },
        { label: isEn ? 'Close overlay' : 'Fechar overlay', keys: ['Esc'] },
      ],
    },
  ];
}

// ─── Key chip ─────────────────────────────────────────────────────────────────

function KeyChip({ label }: { label: string }) {
  return (
    <kbd
      className={cn(
        'inline-flex items-center justify-center min-w-[22px] h-[22px] px-1.5',
        'rounded-md border border-line bg-surface-3',
        'text-[11px] font-mono font-medium text-ink-muted',
        'leading-none shadow-[0_1px_0_0_var(--color-line)]',
      )}
    >
      {label}
    </kbd>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

interface Props {
  open: boolean;
  onClose: () => void;
}

export function KeyboardShortcutsHelp({ open, onClose }: Props) {
  const drawerRef = useRef<HTMLDivElement>(null);
  const { locale } = useT();
  const isEn = locale === 'en';
  const shortcutGroups = getShortcutGroups(isEn);

  // Close on Esc.
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <>
      {/* Scrim — clicking it closes, but no focus trap */}
      <div
        className="fixed inset-0 z-40 bg-ink/10 dark:bg-ink/20 backdrop-blur-[2px]"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Drawer */}
      <div
        ref={drawerRef}
        role="dialog"
        aria-modal="false"
        aria-label={isEn ? 'Keyboard shortcuts' : 'Atalhos de teclado'}
        className={cn(
          // Position
          'fixed right-0 top-0 bottom-0 z-50',
          'w-80 max-w-[90vw]',
          // Visual
          'bg-surface-2 border-l border-line-strong shadow-2xl',
          // Slide-in animation (DESIGN.md drawer spec — translateX(100%)→(0))
          'animate-drawer-in',
          // Allow scrolling if content is taller than viewport
          'overflow-y-auto',
        )}
      >
        {/* Header */}
        <div className="sticky top-0 flex items-center justify-between px-5 py-4 border-b border-line bg-surface-2">
          <div className="flex items-center gap-2">
            <CommandIcon size={15} className="text-ink-muted" />
            <h2 className="text-[14px] font-semibold text-ink">{isEn ? 'Shortcuts' : 'Atalhos'}</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={isEn ? 'Close' : 'Fechar'}
            className={cn(
              'flex items-center justify-center w-7 h-7 rounded-lg',
              'text-ink-muted hover:text-ink hover:bg-surface-3',
              'transition-colors duration-100',
            )}
          >
            <XIcon size={14} />
          </button>
        </div>

        {/* Content */}
        <div className="px-5 py-4 space-y-6">
          {shortcutGroups.map((group) => (
            <div key={group.title}>
              <p className="text-[11px] font-semibold uppercase tracking-wider text-ink-muted mb-2">
                {group.title}
              </p>
              <ul className="space-y-2">
                {group.entries.map((entry) => (
                  <li
                    key={entry.label}
                    className="flex items-center justify-between gap-3"
                  >
                    <span className="text-[13px] text-ink">{entry.label}</span>
                    <div className="flex items-center gap-1 shrink-0">
                      {entry.keys.map((k, i) => (
                        <span key={i} className="flex items-center gap-1">
                          {i > 0 && (
                            <span className="text-[11px] text-ink-soft">{isEn ? 'then' : 'então'}</span>
                          )}
                          <KeyChip label={k} />
                        </span>
                      ))}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          ))}

          {/* Mobile note */}
          <p className="text-[11px] text-ink-soft border-t border-line pt-4">
            {isEn
              ? 'Shortcuts work on desktop. On mobile, use the bottom bar.'
              : 'Atalhos funcionam em desktop. No mobile use a barra inferior.'}
          </p>
        </div>
      </div>
    </>
  );
}
